import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import { useTranslations } from '../../context/TranslationContext';

// Design System — StatusSummary / StepsBreakdown
// Per-recruitment-step breakdown shown inside the Tooltip of a status item:
// one row per step with its applicant count and step name. A step that
// carries a `link` (legacy `applicant_link`) renders as an anchor to the
// filtered applicant list — the navigation legacy `Legends` offered. A
// `withheld` step (one the viewer may not see into) shows a lock in place of
// the count and never links: its list would be empty.

const StepsBreakdown = ({ steps }) => {
    const { t } = useTranslations();
    return (
        <div className="ds-status-summary__steps">
            {steps.map((step) => {
                const Row = step.link ? 'a' : 'span';
                return (
                    <Row
                        className={classNames('ds-status-summary__step', {
                            'ds-status-summary__step--link': step.link,
                            'ds-status-summary__step--withheld': step.withheld,
                        })}
                        href={step.link || undefined}
                        key={step.id}
                    >
                        <span className="ds-status-summary__step-count">
                            {step.withheld ? (
                                <Icon
                                    value="lock-01"
                                    noMargin
                                    className="ds-status-summary__step-lock"
                                    title={t('This step is not shared with you')}
                                />
                            ) : (
                                step.count
                            )}
                        </span>
                        <span className="ds-status-summary__step-label">{step.label}</span>
                    </Row>
                );
            })}
        </div>
    );
};

StepsBreakdown.propTypes = {
    /** Normalised steps: `{ id, label, count, link, withheld }` per recruitment step. */
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.node,
            count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            /** Optional applicant-list URL; the row becomes a link when set. */
            link: PropTypes.string,
            /** The viewer may not see this step: lock instead of a count, no link. */
            withheld: PropTypes.bool,
        }),
    ).isRequired,
};

export default StepsBreakdown;
